import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ShieldAlert,
  KeyRound,
  Eye,
  EyeOff,
  Plus,
  Trash2,
  AlertTriangle,
  Loader2,
  X,
  Save,
} from "lucide-react";
import { getTenants } from "@/api/endpoints/tenants";
import {
  listTenantSecrets,
  readTenantSecret,
  writeTenantSecret,
  deleteTenantSecret,
} from "@/api/endpoints/adminVault";

// Vault explorer — scoped to tenants/<slug>/* paths.
// Reads + writes + deletes are audit-logged on the backend; secret
// values never leave the viewing session (no query cache persist).

function KVEditor({
  initial,
  onChange,
}: {
  initial: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}) {
  const [rows, setRows] = useState<Array<[string, string]>>(
    Object.entries(initial).length > 0 ? Object.entries(initial) : [["", ""]],
  );
  const push = (next: Array<[string, string]>) => {
    setRows(next);
    onChange(Object.fromEntries(next.filter(([k]) => k.trim())));
  };
  return (
    <div className="space-y-1.5">
      {rows.map(([k, v], i) => (
        <div key={i} className="flex gap-2">
          <input
            type="text"
            value={k}
            onChange={(e) => {
              const next = [...rows];
              next[i] = [e.target.value, v];
              push(next);
            }}
            placeholder="key"
            className="w-32 rounded border border-gray-700 bg-gray-800 px-2 py-1 font-mono text-xs text-gray-100 focus:border-blue-500 focus:outline-none"
          />
          <input
            type="text"
            value={v}
            onChange={(e) => {
              const next = [...rows];
              next[i] = [k, e.target.value];
              push(next);
            }}
            placeholder="value"
            className="flex-1 rounded border border-gray-700 bg-gray-800 px-2 py-1 font-mono text-xs text-gray-100 focus:border-blue-500 focus:outline-none"
          />
          <button
            type="button"
            onClick={() => push(rows.filter((_, idx) => idx !== i))}
            className="rounded border border-gray-700 px-2 text-xs text-gray-500 hover:bg-gray-800"
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => push([...rows, ["", ""]])}
        className="flex items-center gap-1 rounded border border-gray-700 px-2 py-1 text-xs text-gray-400 hover:bg-gray-800"
      >
        <Plus className="h-3 w-3" /> Ajouter une cle
      </button>
    </div>
  );
}

export default function AdminVault() {
  const queryClient = useQueryClient();
  const tenantsQuery = useQuery({ queryKey: ["tenants"], queryFn: getTenants });
  const [slug, setSlug] = useState<string>("");
  const [modalMode, setModalMode] = useState<"view" | "edit" | "create" | null>(
    null,
  );
  const [modalName, setModalName] = useState("");
  const [modalData, setModalData] = useState<Record<string, string>>({});
  const [revealed, setRevealed] = useState(false);

  const secretsQuery = useQuery({
    queryKey: ["admin-vault", slug],
    queryFn: () => listTenantSecrets(slug),
    enabled: !!slug,
  });

  const readMut = useMutation({
    mutationFn: (name: string) => readTenantSecret(slug, name),
    onSuccess: (r, name) => {
      setModalName(name);
      setModalData(r.data);
      setModalMode("view");
      setRevealed(false);
    },
  });

  const writeMut = useMutation({
    mutationFn: ({ name, data }: { name: string; data: Record<string, string> }) =>
      writeTenantSecret(slug, name, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-vault", slug] });
      setModalMode(null);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (name: string) => deleteTenantSecret(slug, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-vault", slug] });
    },
  });

  const openCreate = () => {
    setModalName("");
    setModalData({});
    setModalMode("create");
    setRevealed(true);
  };

  const openEdit = async (name: string) => {
    const r = await readTenantSecret(slug, name);
    setModalName(name);
    setModalData(r.data);
    setModalMode("edit");
    setRevealed(true);
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <ShieldAlert className="h-7 w-7 text-amber-400" />
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Vault</h1>
          <p className="text-sm text-gray-400">
            Secrets par tenant — chemin <code className="font-mono text-xs">tenants/&lt;slug&gt;/*</code>.
            Lectures et ecritures auditees cote backend, les valeurs ne sont
            jamais logguees.
          </p>
        </div>
      </div>

      {/* Tenant picker */}
      <section className="rounded-lg border border-gray-800 bg-gray-900 p-4">
        <label className="mb-1 block text-xs uppercase tracking-wide text-gray-500">
          Tenant
        </label>
        <select
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none"
        >
          <option value="">— Choisir un tenant —</option>
          {(tenantsQuery.data ?? []).map((t) => (
            <option key={t.id} value={t.slug}>
              {t.name} ({t.slug})
            </option>
          ))}
        </select>
      </section>

      {/* Secrets list */}
      {slug && (
        <section className="rounded-lg border border-gray-800 bg-gray-900 p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-200">
              <KeyRound className="h-4 w-4" /> Secrets{" "}
              <span className="font-mono text-gray-500">tenants/{slug}/</span>
              {secretsQuery.data && (
                <span className="ml-2 text-gray-500">
                  ({secretsQuery.data.secrets.length})
                </span>
              )}
            </h2>
            <button
              onClick={openCreate}
              className="flex items-center gap-1 rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500"
            >
              <Plus className="h-3 w-3" /> Nouveau secret
            </button>
          </div>
          {secretsQuery.isLoading && (
            <p className="text-sm text-gray-500">Chargement...</p>
          )}
          {secretsQuery.error && (
            <p className="flex items-center gap-2 text-sm text-red-400">
              <AlertTriangle className="h-4 w-4" />{" "}
              {(secretsQuery.error as Error).message}
            </p>
          )}
          {secretsQuery.data && secretsQuery.data.secrets.length === 0 && (
            <p className="text-sm text-gray-500">
              Aucun secret. Cree le premier pour y ranger les credentials
              specifiques a ce tenant.
            </p>
          )}
          {secretsQuery.data && secretsQuery.data.secrets.length > 0 && (
            <ul className="divide-y divide-gray-800">
              {secretsQuery.data.secrets.map((name) => (
                <li
                  key={name}
                  className="flex items-center justify-between py-2"
                >
                  <span className="font-mono text-sm text-gray-200">{name}</span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => readMut.mutate(name)}
                      disabled={readMut.isPending}
                      className="rounded border border-gray-700 bg-gray-800 px-2 py-1 text-xs text-gray-200 hover:bg-gray-700 disabled:opacity-50"
                    >
                      <Eye className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => openEdit(name)}
                      className="rounded border border-blue-900/50 bg-blue-900/20 px-2 py-1 text-xs text-blue-300 hover:bg-blue-900/40"
                    >
                      Editer
                    </button>
                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            `Supprimer le secret "${name}" ? Soft-delete Vault — encore restaurable via CLI.`,
                          )
                        ) {
                          deleteMut.mutate(name);
                        }
                      }}
                      disabled={
                        deleteMut.isPending && deleteMut.variables === name
                      }
                      className="rounded border border-red-900/50 bg-red-900/20 px-2 py-1 text-xs text-red-300 hover:bg-red-900/40 disabled:opacity-50"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* Modal view/edit/create */}
      {modalMode && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setModalMode(null)}
        >
          <div
            className="w-full max-w-xl rounded-lg border border-gray-700 bg-gray-900 p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-200">
                <KeyRound className="h-4 w-4" />
                {modalMode === "create"
                  ? "Nouveau secret"
                  : modalMode === "edit"
                    ? `Editer ${modalName}`
                    : `Voir ${modalName}`}
                <span className="font-mono text-xs text-gray-500">
                  — tenants/{slug}/
                </span>
              </h3>
              <button
                onClick={() => setModalMode(null)}
                className="rounded p-1 text-gray-500 hover:bg-gray-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {modalMode === "create" && (
              <div className="mb-3">
                <label className="mb-1 block text-xs uppercase tracking-wide text-gray-500">
                  Nom du secret
                </label>
                <input
                  type="text"
                  value={modalName}
                  onChange={(e) => setModalName(e.target.value)}
                  placeholder="ex: proxmox-backup, gitlab-token, smtp"
                  pattern="[a-z0-9][a-z0-9_-]*"
                  className="w-full rounded border border-gray-700 bg-gray-800 px-2 py-1 font-mono text-xs text-gray-100 focus:border-blue-500 focus:outline-none"
                />
              </div>
            )}

            <div className="mb-3 flex items-center gap-2 text-xs">
              <button
                onClick={() => setRevealed((r) => !r)}
                className="flex items-center gap-1 rounded border border-gray-700 px-2 py-1 text-gray-400 hover:bg-gray-800"
              >
                {revealed ? (
                  <>
                    <EyeOff className="h-3 w-3" /> Masquer les valeurs
                  </>
                ) : (
                  <>
                    <Eye className="h-3 w-3" /> Reveler les valeurs
                  </>
                )}
              </button>
            </div>

            {modalMode === "view" ? (
              <div className="space-y-1 text-xs">
                {Object.entries(modalData).map(([k, v]) => (
                  <div key={k} className="flex gap-2">
                    <span className="w-32 font-mono text-gray-400">{k}</span>
                    <code className="flex-1 truncate rounded bg-gray-950 px-2 py-1 font-mono text-gray-200">
                      {revealed ? v : "•".repeat(Math.min(24, v.length))}
                    </code>
                    <button
                      onClick={() => navigator.clipboard.writeText(v)}
                      className="rounded border border-gray-700 px-2 py-1 text-gray-400 hover:bg-gray-800"
                    >
                      Copier
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <KVEditor initial={modalData} onChange={setModalData} />
            )}

            {writeMut.isError && (
              <p className="mt-2 flex items-center gap-2 text-sm text-red-400">
                <AlertTriangle className="h-4 w-4" />{" "}
                {(writeMut.error as Error).message}
              </p>
            )}

            {modalMode !== "view" && (
              <button
                onClick={() =>
                  writeMut.mutate({ name: modalName, data: modalData })
                }
                disabled={
                  !modalName || Object.keys(modalData).length === 0 || writeMut.isPending
                }
                className="mt-4 flex w-full items-center justify-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
              >
                {writeMut.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Enregistrer
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
